import { IdConta, IdInbox, IdQuadro, IdUsuario } from "../common-types";
import { GenericServiceWithoutGetAll } from "../generic-service";
import { IMetaParams } from "../templates-wpp-meta/meta-wpp-template.service";

export type TipoAutomacaoAgendada = 'NENHUMA' | 'MENSAGEM' | 'TEMPLATE' | 'META_TEMPLATE' | 'BOT';

/**
 * Conteúdo da trilha META do quadro (atendimentos de inbox WHATSAPP_META e
 * WHATSAPP_HYBRID_COEXISTENCE): nada ou template oficial aprovado.
 */
export type TipoAutomacaoMeta = 'NENHUMA' | 'META_TEMPLATE';

export type CreateQuadroKanban = {
    nome: string;
    visibilidade: 'GLOBAL' | 'PESSOAL';
    tipo_mensagem_movimentacao: 'NENHUMA' | 'MENSAGEM' | 'TEMPLATE';
    mensagem_movimentacao_entrada?: string;
    template_movimentacao_entrada?: string;
    tipo_mensagem_movimentacao_meta?: TipoAutomacaoMeta;
    id_meta_template_movimentacao_entrada?: string;
    meta_template_params_movimentacao_entrada?: IMetaParams;
    tipo_automacao_agendada_meta?: TipoAutomacaoMeta;
    tipo_automacao_agendada?: TipoAutomacaoAgendada;
    mensagem_automacao_agendada?: string;
    template_automacao_agendada?: string;
    id_meta_template_automacao_agendada?: string;
    meta_template_params_automacao_agendada?: IMetaParams;
    id_bot_automacao_agendada?: string;
    dias_automacao_agendada?: number;
    horario_automacao_agendada?: string;
    idConta: IdConta;
    idUsuario?: IdUsuario;
    inboxes?: IdInbox[];
}

export type UpdateQuadroKanban = Partial<CreateQuadroKanban> & { id: IdQuadro }

export type ResponseQuadroKanban = {
    id: IdQuadro;
    nome: string;
    tipo_mensagem_movimentacao: 'NENHUMA' | 'MENSAGEM' | 'TEMPLATE';
    visibilidade: 'GLOBAL' | 'PESSOAL';
    mensagem_movimentacao_entrada?: string;
    template_movimentacao_entrada?: string;
    tipo_mensagem_movimentacao_meta?: TipoAutomacaoMeta;
    id_meta_template_movimentacao_entrada?: string | null;
    meta_template_params_movimentacao_entrada?: IMetaParams | null;
    tipo_automacao_agendada_meta?: TipoAutomacaoMeta;
    metaTemplateMovimentacao?: { id: string; idInbox: IdInbox; displayName: string } | null;
    tipo_automacao_agendada?: TipoAutomacaoAgendada;
    mensagem_automacao_agendada?: string | null;
    template_automacao_agendada?: string | null;
    id_meta_template_automacao_agendada?: string | null;
    meta_template_params_automacao_agendada?: IMetaParams | null;
    id_bot_automacao_agendada?: string | null;
    dias_automacao_agendada?: number | null;
    horario_automacao_agendada?: string | null;
    metaTemplateAutomacao?: { id: string; idInbox: IdInbox; displayName: string } | null;
    idConta: IdConta;
    idUsuario?: IdUsuario;
    inboxes?: IdInbox[];
    del: boolean;
    createdAt?: string;
    /** Posição escolhida por este usuário. `null` = nunca ordenado, vai para o fim. */
    ordem?: number | null;
}

type QuadroInboxRaw = {
    idQuadro: string;
    idInbox: IdInbox;
}

export type QuadroOrdemRaw = {
    idQuadro: IdQuadro;
    ordem: number;
}

export class QuadroKanbanService extends GenericServiceWithoutGetAll<CreateQuadroKanban, UpdateQuadroKanban, ResponseQuadroKanban> {
    constructor() { super('quadros') }

    private mapInboxes(quadro: any): ResponseQuadroKanban {
        return {
            ...quadro,
            inboxes: (quadro.inboxes as QuadroInboxRaw[] ?? []).map(qi => qi.idInbox),
        };
    }

    async getAll(search?: string, idConta?: string, idUsuario?: string): Promise<ResponseQuadroKanban[]> {
        const params = (search || idConta || idUsuario) ? { search, idConta, idUsuario } : undefined;
        const res = await this.f.get('/', params) as any[];
        return res.map(q => this.mapInboxes(q));
    }

    async getById(id: IdQuadro): Promise<ResponseQuadroKanban> {
        const res = await this.f.get(`/${id}`) as any;
        return this.mapInboxes(res);
    }

    async updateDefaultBoardNames(idConta: IdConta, names: Record<string, string>): Promise<void> {
        await this.f.patch(`/${idConta}/default-board-names`, names);
    }

    /**
     * Grava a ordem dos quadros deste usuário.
     *
     * Manda a lista visível inteira, na ordem final: o servidor reescreve os vínculos
     * desses quadros a partir do índice de cada um.
     */
    async reordenar(quadros: IdQuadro[]): Promise<QuadroOrdemRaw[]> {
        return await this.f.put('/ordem', { quadros }) as QuadroOrdemRaw[];
    }
}